import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, Link } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get, put, getAuthHeaders } from '../utils/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function money(n) {
  const x = Number(n);
  if (Number.isNaN(x)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(x);
}

function fmtDate(gmt) {
  if (gmt == null) return '—';
  try {
    const d = new Date(gmt);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

/** Display-only: show immediate sub-category; strip Kotilinga roots (API unchanged). */
function displayCategoryLabel(raw) {
  if (raw == null || String(raw).trim() === '') return '—';
  const parts = String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return parts[0] || '—';
  const roots = new Set(['kotilinga kshetram', 'kadambavanam']);
  const rest = parts.filter((p) => !roots.has(p.toLowerCase()));
  if (rest.length) return rest.join(', ');
  return parts[0] || '—';
}

export default function KotilingaOrderInventoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [skus, setSkus] = React.useState([]);
  const [summary, setSummary] = React.useState({
    buyer_count: 0,
    revenue_completed: 0,
    pledge_receivables: 0,
  });
  const [summaryByCategory, setSummaryByCategory] = React.useState([]);
  const [q, setQ] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [dialogRow, setDialogRow] = React.useState(null);
  const [sponsors, setSponsors] = React.useState([]);
  const [sponsorsLoading, setSponsorsLoading] = React.useState(false);
  const [sponsorPage, setSponsorPage] = React.useState(1);
  const [sponsorQ, setSponsorQ] = React.useState('');
  const [sponsorQDebounced, setSponsorQDebounced] = React.useState('');
  const [sponsorLetter, setSponsorLetter] = React.useState('');
  const [sponsorsMeta, setSponsorsMeta] = React.useState({
    total_count: 0,
    total_pages: 1,
    show_engrave: false,
  });
  /** @type {[Record<string, { engrave: string, notes: string }>, Function]} */
  const [draftByContact, setDraftByContact] = React.useState({});
  const [savingContactId, setSavingContactId] = React.useState(null);
  const [exporting, setExporting] = React.useState(false);
  const [fullPdfLoading, setFullPdfLoading] = React.useState(false);
  const sponsorsAbortRef = React.useRef(null);
  /** @type {[{ contactId: number, sponsorName: string } | null, Function]} */
  const [ordersDialog, setOrdersDialog] = React.useState(null);
  const [ordersRows, setOrdersRows] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [orderReadId, setOrderReadId] = React.useState(null);
  const [orderReadLoading, setOrderReadLoading] = React.useState(false);
  const [orderReadErr, setOrderReadErr] = React.useState('');
  const [orderReadPayload, setOrderReadPayload] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setSponsorQDebounced(sponsorQ), 350);
    return () => clearTimeout(t);
  }, [sponsorQ]);

  React.useEffect(() => {
    let c = false;
    (async () => {
      setLoading(true);
      setErr('');
      try {
        const res = await get('/ldb/kotilinga-order-inventory/skus');
        if (!c) {
          setSkus(Array.isArray(res?.data) ? res.data : []);
          if (res?.summary && typeof res.summary === 'object') {
            setSummary({
              buyer_count: Number(res.summary.buyer_count || 0),
              revenue_completed: Number(res.summary.revenue_completed || 0),
              pledge_receivables: Number(res.summary.pledge_receivables || 0),
            });
          }
          setSummaryByCategory(Array.isArray(res?.summary_by_category) ? res.summary_by_category : []);
        }
      } catch (e) {
        if (!c) setErr(e?.message || 'Failed to load');
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => { c = true; };
  }, []);

  const catalogCategoryOptions = React.useMemo(() => {
    const labels = new Set();
    let hasUncategorized = false;
    for (const r of skus) {
      const d = displayCategoryLabel(r.category_label);
      if (d === '—') hasUncategorized = true;
      else labels.add(d);
    }
    const sorted = Array.from(labels).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return { labels: sorted, hasUncategorized };
  }, [skus]);

  const filtered = React.useMemo(() => {
    let rows = skus;
    if (categoryFilter !== 'all') {
      if (categoryFilter === '__uncategorized__') {
        rows = rows.filter((r) => displayCategoryLabel(r.category_label) === '—');
      } else {
        rows = rows.filter((r) => displayCategoryLabel(r.category_label) === categoryFilter);
      }
    }
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.category_label || '').toLowerCase().includes(s) ||
        String(r.product_name || '').toLowerCase().includes(s) ||
        String(r.variation_name || '').toLowerCase().includes(s) ||
        String(r.product_id).includes(s) ||
        String(r.sponsor_search || '').includes(s)
    );
  }, [skus, q, categoryFilter]);

  const loadSponsorsPage = React.useCallback(async () => {
    if (!dialogRow) return;
    try {
      sponsorsAbortRef.current?.abort();
    } catch (_) {}
    const ac = new AbortController();
    sponsorsAbortRef.current = ac;
    const pid = Number(dialogRow.product_id);
    const vid = dialogRow.variation_id != null && dialogRow.variation_id !== '' ? Number(dialogRow.variation_id) : 0;
    const params = {
      product_id: pid,
      variation_id: vid,
      page: sponsorPage,
      page_size: 50,
    };
    if (sponsorQDebounced) params.q = sponsorQDebounced;
    if (sponsorLetter) params.letter = sponsorLetter;
    try {
      if (process.env.NODE_ENV === 'development') {
        const qs = new URLSearchParams(params).toString();
        console.info('[Sponsors UI] GET /ldb/kotilinga-order-inventory/sponsors?' + qs);
      }
    } catch (_) {}
    setSponsorsLoading(true);
    try {
      const res = await get('/ldb/kotilinga-order-inventory/sponsors', params, { signal: ac.signal });
      if (ac.signal.aborted) return;
      const list = Array.isArray(res?.data) ? res.data : [];
      setSponsors(list);
      const m = res?.meta || {};
      setSponsorsMeta({
        total_count: Number(m.total_count || 0),
        total_pages: Math.max(1, Number(m.total_pages || 1)),
        show_engrave: !!m.show_engrave,
      });
      const drafts = {};
      for (const s of list) {
        if (s.contact_id == null) continue;
        drafts[String(s.contact_id)] = {
          engrave: s.detail?.engrave_name ?? '',
          notes: s.detail?.notes ?? '',
        };
      }
      setDraftByContact(drafts);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setErr(e?.message || 'Failed to load sponsors');
    } finally {
      if (!ac.signal.aborted) setSponsorsLoading(false);
    }
  }, [dialogRow, sponsorPage, sponsorQDebounced, sponsorLetter]);

  React.useEffect(() => {
    if (!dialogRow) return;
    loadSponsorsPage();
  }, [dialogRow, loadSponsorsPage]);

  const openSponsors = (row) => {
    setSponsorLetter('');
    setSponsorQ('');
    setSponsorQDebounced('');
    setSponsorPage(1);
    setDialogRow(row);
  };

  const closeSponsors = () => {
    setDialogRow(null);
    setSponsors([]);
    setOrdersDialog(null);
    setOrdersRows([]);
    setOrderReadId(null);
    setOrderReadPayload(null);
    setOrderReadErr('');
  };

  const openOrdersForSponsor = async (s) => {
    const cid = s.contact_id;
    if (!dialogRow || cid == null || cid <= 0) return;
    setOrdersDialog({ contactId: cid, sponsorName: s.sponsor_name || '' });
    setOrdersLoading(true);
    setOrdersRows([]);
    try {
      const pid = Number(dialogRow.product_id);
      const vid = dialogRow.variation_id != null && dialogRow.variation_id !== '' ? Number(dialogRow.variation_id) : 0;
      const res = await get('/ldb/kotilinga-order-inventory/sponsors/orders', {
        product_id: pid,
        variation_id: vid,
        contact_id: cid,
      });
      setOrdersRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setErr(e?.message || 'Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const closeOrdersDialog = () => {
    setOrdersDialog(null);
    setOrdersRows([]);
    setOrderReadId(null);
    setOrderReadPayload(null);
    setOrderReadErr('');
  };

  const openOrderRead = async (orderId) => {
    const id = Number(orderId);
    if (!Number.isFinite(id) || id <= 0) return;
    setOrderReadId(id);
    setOrderReadLoading(true);
    setOrderReadErr('');
    setOrderReadPayload(null);
    try {
      const res = await get(`/ldb/orders/${id}`);
      setOrderReadPayload({
        order: res?.order || null,
        items: Array.isArray(res?.items) ? res.items : [],
      });
    } catch (e) {
      setOrderReadErr(e?.message || 'Failed to load order');
    } finally {
      setOrderReadLoading(false);
    }
  };

  const closeOrderRead = () => {
    setOrderReadId(null);
    setOrderReadPayload(null);
    setOrderReadErr('');
  };

  const saveDetail = async (contactId) => {
    if (!dialogRow || contactId == null) return;
    const d = draftByContact[String(contactId)] || { engrave: '', notes: '' };
    const row = sponsors.find((x) => x.contact_id === contactId);
    const engravePersist = sponsorsMeta.show_engrave
      ? d.engrave
      : (row?.detail?.engrave_name ?? '');
    setSavingContactId(contactId);
    setErr('');
    try {
      await put('/ldb/kotilinga-order-inventory/sponsor-detail', {
        customer_id: contactId,
        product_id: dialogRow.product_id,
        variation_id: dialogRow.variation_id || 0,
        engrave_name: engravePersist,
        notes: d.notes,
      });
      setSponsors((prev) =>
        prev.map((s) =>
          s.contact_id === contactId
            ? { ...s, detail: { engrave_name: d.engrave, notes: d.notes } }
            : s
        )
      );
    } catch (e) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSavingContactId(null);
    }
  };

  const downloadCsv = async () => {
    if (!dialogRow || exporting) return;
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        product_id: String(dialogRow.product_id),
        variation_id: String(dialogRow.variation_id || 0),
      });
      if (sponsorQDebounced) qs.set('q', sponsorQDebounced);
      if (sponsorLetter) qs.set('letter', sponsorLetter);
      let baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      if (!baseUrl && typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)) {
        baseUrl = 'http://localhost:5001';
      }
      const url = `${baseUrl.replace(/\/$/, '')}/ldb/kotilinga-order-inventory/sponsors/export.csv?${qs}`;
      const r = await fetch(url, { headers: { ...getAuthHeaders() } });
      if (!r.ok) throw new Error('CSV export failed');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sponsors-${dialogRow.product_id}-${dialogRow.variation_id || 0}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      setErr(e?.message || 'CSV failed');
    } finally {
      setExporting(false);
    }
  };

  const downloadPdfPage = () => {
    if (!dialogRow || !sponsors.length) return;
    const showEngrave = sponsorsMeta.show_engrave;
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(10);
    doc.text(
      `${dialogRow.product_name}${dialogRow.variation_id ? ` · ${dialogRow.variation_name}` : ''}`,
      14,
      12
    );
    doc.text(`Page ${sponsorPage} of ${sponsorsMeta.total_pages} · ${sponsorsMeta.total_count} sponsors`, 14, 18);
    const head = showEngrave
      ? [['Name', 'Paid', 'Orders', 'Engrave', 'Notes']]
      : [['Name', 'Paid', 'Orders', 'Notes']];
    const body = sponsors.map((s) => {
      const d = s.contact_id != null ? draftByContact[String(s.contact_id)] : { engrave: '', notes: '' };
      const row = [
        s.sponsor_name,
        money(s.total_paid),
        String(s.orders_count ?? s.ordersCount ?? 0),
        ...(showEngrave ? [d?.engrave || s.detail?.engrave_name || ''] : []),
        d?.notes || s.detail?.notes || '',
      ];
      return row;
    });
    autoTable(doc, { startY: 22, head, body, styles: { fontSize: 7 }, headStyles: { fillColor: [0, 105, 120] } });
    doc.save(`sponsors-page-${sponsorPage}.pdf`);
  };

  const alphaKeys = React.useMemo(
    () => ['', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)), '#'],
    []
  );

  const downloadFullCatalogPdf = async () => {
    setFullPdfLoading(true);
    setErr('');
    try {
      const res = await get('/ldb/kotilinga-order-inventory/full-export');
      const sum = res?.summary || {};
      const skusList = Array.isArray(res?.skus) ? res.skus : [];
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const margin = 36;
      let y = 32;
      doc.setFontSize(14);
      doc.text('Kotilinga catalog & sponsors (full export)', margin, y);
      y += 16;
      doc.setFontSize(8);
      doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y);
      y += 12;
      doc.text(
        `Catalog SKUs: ${Number(res?.catalog_item_count || skusList.length).toLocaleString()} | Unique sponsors (incl. pledges): ${Number(sum.buyer_count || 0).toLocaleString()} | Revenue (completed): ${money(sum.revenue_completed)} | Receivables (pledge): ${money(sum.pledge_receivables)}`,
        margin,
        y
      );
      y += 14;
      autoTable(doc, {
        startY: y,
        head: [['Category', 'Product', 'Variation', 'Product ID', 'Var ID', 'Revenue', 'Sponsors']],
        body: skusList.map((s) => [
          displayCategoryLabel(s.category_label),
          String(s.product_name || ''),
          String(s.variation_name || ''),
          String(s.product_id),
          String(s.variation_id ?? 0),
          money(s.revenue_completed),
          String(s.unique_buyers ?? 0),
        ]),
        styles: { fontSize: 6, cellPadding: 3 },
        headStyles: { fillColor: [0, 105, 120] },
        margin: { left: margin, right: margin },
      });
      y = (doc.lastAutoTable?.finalY || y) + 20;
      for (const s of skusList) {
        const sponsors = Array.isArray(s.sponsors) ? s.sponsors : [];
        if (sponsors.length === 0) continue;
        const title = `${s.product_name}${s.variation_id ? ` · ${s.variation_name}` : ''} (#${s.product_id} / var ${s.variation_id ?? 0})`;
        if (y > doc.internal.pageSize.getHeight() - 120) {
          doc.addPage();
          y = 36;
        }
        doc.setFontSize(9);
        doc.text(title.slice(0, 95) + (title.length > 95 ? '…' : ''), margin, y);
        y += 10;
        if (s.sponsors_truncated) {
          doc.setFontSize(7);
          doc.setTextColor(180, 0, 0);
          doc.text('Sponsor list truncated at 8000 rows for this SKU.', margin, y);
          doc.setTextColor(0, 0, 0);
          y += 10;
        }
        autoTable(doc, {
          startY: y,
          head: [['Sponsor', 'Paid', 'Orders', 'Engrave', 'Notes']],
          body: sponsors.map((p) => [
            String(p.sponsor_name || ''),
            money(p.total_paid),
            String(p.orders_count ?? 0),
            String(p.engrave_name || '').slice(0, 80),
            String(p.notes || '').slice(0, 80),
          ]),
          styles: { fontSize: 5, cellPadding: 2 },
          headStyles: { fillColor: [70, 90, 100] },
          margin: { left: margin, right: margin },
        });
        y = (doc.lastAutoTable?.finalY || y) + 14;
      }
      doc.save(`kotilinga-catalog-sponsors-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      setErr(e?.message || 'Full PDF failed (timeout if catalog is very large)');
    } finally {
      setFullPdfLoading(false);
    }
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <IconButton onClick={() => navigate('/kotilinga/sponsorships')} size="small" title="Kotilinga dashboard">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ minWidth: 280 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>Catalog &amp; sponsors</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Order-based catalog (completed revenue). See docs for classic inventory gaps.
            </Typography>
          </Box>
        </Box>
        <Tooltip title="All SKUs + sponsor lists per SKU (may take a minute)">
          <span>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              disabled={fullPdfLoading || loading}
              onClick={downloadFullCatalogPdf}
            >
              {fullPdfLoading ? 'Building PDF…' : 'Download full PDF'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Summary (aligned with classic inventory stats)
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Catalog items (SKUs)</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{loading ? '—' : skus.length.toLocaleString()}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Revenue (completed orders)</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(summary.revenue_completed)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Account receivables (pledge pending)</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(summary.pledge_receivables)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Unique sponsors (incl. pledges)</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{summary.buyer_count.toLocaleString()}</Typography>
          </Paper>
        </Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          By category
        </Typography>
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Sponsors</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue (completed)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Account receivables (pledge)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summaryByCategory.map((row) => (
                <TableRow key={row.category_name}>
                  <TableCell>{row.category_name}</TableCell>
                  <TableCell align="right">{Number(row.buyer_count || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{money(row.revenue_completed)}</TableCell>
                  <TableCell align="right">{money(row.pledge_receivables)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: 2, borderColor: 'divider' } }}>
                <TableCell>Total (all)</TableCell>
                <TableCell align="right">{summary.buyer_count.toLocaleString()}</TableCell>
                <TableCell align="right">{money(summary.revenue_completed)}</TableCell>
                <TableCell align="right">{money(summary.pledge_receivables)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            alignItems: 'center',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(260px, 2fr) minmax(200px, 1.4fr)',
              md: 'minmax(360px, 3fr) minmax(220px, 1.6fr) auto',
            },
          }}
        >
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Product, variation, sponsor…"
              InputLabelProps={{ shrink: true }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="koi-cat-filter" shrink>Category</InputLabel>
              <Select
                labelId="koi-cat-filter"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
                notched
              >
                <MenuItem value="all">All</MenuItem>
                {catalogCategoryOptions.hasUncategorized ? (
                  <MenuItem value="__uncategorized__">Uncategorized</MenuItem>
                ) : null}
                {catalogCategoryOptions.labels.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', pt: { xs: 0, md: 0.5 } }}>
              {(categoryFilter !== 'all' || q.trim() !== '') && (
                <Chip
                  label="Clear filters"
                  size="small"
                  onClick={() => {
                    setCategoryFilter('all');
                    setQ('');
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                Total: {loading ? '—' : filtered.length.toLocaleString()} items
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Variation</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue (paid)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Sponsors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={`${r.product_id}-${r.variation_id}`} hover>
                  <TableCell sx={{ maxWidth: 200 }} title={r.category_label || ''}>
                    {displayCategoryLabel(r.category_label)}
                  </TableCell>
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell>{r.variation_id ? r.variation_name : '—'}</TableCell>
                  <TableCell align="right">{money(r.revenue_completed)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}
                    >
                      <Typography component="span" variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number(r.unique_buyers || 0).toLocaleString()}
                      </Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => openSponsors(r)}
                        aria-label="Open sponsors"
                        sx={{ ml: 0.5 }}
                      >
                        <PeopleIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!dialogRow}
        onClose={closeSponsors}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { width: 'min(1200px, 96vw)', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1, pr: 1 }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h6">Sponsors</Typography>
            <Typography variant="body2" color="text.secondary" title={dialogRow?.product_name}>
              {dialogRow?.product_name}
              {dialogRow?.variation_id ? ` · ${dialogRow?.variation_name}` : ''}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ fontFamily: 'ui-monospace, monospace', mt: 0.25 }}
            >
              Product ID: <strong>{dialogRow?.product_id ?? '—'}</strong>
              {' · '}
              Variation ID:{' '}
              <strong>
                {dialogRow != null
                  ? dialogRow.variation_id != null && dialogRow.variation_id !== ''
                    ? dialogRow.variation_id
                    : 0
                  : '—'}
              </strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sponsorsMeta.total_count.toLocaleString()} sponsors · 50 per page · CSV = full list (filtered)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="PDF of this page only">
              <span>
                <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={downloadPdfPage} disabled={!sponsors.length}>
                  PDF
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={downloadCsv}
              disabled={exporting}
            >
              {exporting ? '…' : 'CSV'}
            </Button>
            <IconButton onClick={closeSponsors} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search name…"
            value={sponsorQ}
            onChange={(e) => {
              setSponsorQ(e.target.value);
              setSponsorPage(1);
            }}
            sx={{ mb: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              A–Z
            </Typography>
            {alphaKeys.map((L) => (
              <Chip
                key={L || 'all'}
                label={L === '' ? 'All' : L}
                size="small"
                color={sponsorLetter === L ? 'primary' : 'default'}
                onClick={() => {
                  setSponsorLetter(L);
                  setSponsorPage(1);
                }}
                sx={{ minWidth: 36 }}
              />
            ))}
          </Box>
          {sponsorsMeta.show_engrave && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Engrave enabled (unit price ≥ $10,000)
            </Typography>
          )}
          {sponsorsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                <strong>Paid</strong> and <strong>Orders</strong> use only <strong>completed</strong> orders for this product. Click Orders to open the list.
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: sponsorsMeta.show_engrave
                    ? 'minmax(140px,1.1fr) 92px 48px minmax(200px,1.4fr) 44px'
                    : 'minmax(140px,1.15fr) 92px 48px minmax(200px,1.5fr) 44px',
                  gap: 1,
                  alignItems: 'start',
                  py: 0.75,
                  px: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              >
                <span>Name</span>
                <span>Paid</span>
                <span>Orders</span>
                <span>{sponsorsMeta.show_engrave ? 'Engrave / notes' : 'Notes'}</span>
                <span />
              </Box>
              <Box sx={{ maxHeight: 'min(420px, 50vh)', overflow: 'auto' }}>
                {sponsors.map((s) => {
                  const cid = s.contact_id;
                  const key = cid != null ? String(cid) : `g-${s.sponsor_name}`;
                  const draft = cid != null ? draftByContact[key] : { engrave: '', notes: '' };
                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: sponsorsMeta.show_engrave
                          ? 'minmax(140px,1.1fr) 92px 48px minmax(200px,1.4fr) 44px'
                          : 'minmax(140px,1.15fr) 92px 48px minmax(200px,1.5fr) 44px',
                        gap: 1,
                        alignItems: 'start',
                        py: 1,
                        px: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ pt: 0.5 }} noWrap title={s.sponsor_name}>
                        {cid ? (
                          <Link to={`/devotees/${encodeURIComponent(cid)}`}>{s.sponsor_name}</Link>
                        ) : (
                          s.sponsor_name
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ pt: 0.5, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>
                        {money(s.total_paid)}
                      </Typography>
                      <Box sx={{ textAlign: 'center', pt: 0.25 }}>
                        {cid > 0 ? (
                          <Button
                            size="small"
                            variant="text"
                            sx={{ minWidth: 36, p: 0, fontWeight: 700 }}
                            onClick={() => openOrdersForSponsor(s)}
                            title="Completed orders for this product"
                          >
                            {Number(s.orders_count ?? s.ordersCount ?? 0).toLocaleString()}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 1 }}>—</Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                        {sponsorsMeta.show_engrave && (
                          <TextField
                            size="small"
                            fullWidth
                            label="Engrave"
                            placeholder="—"
                            value={draft.engrave}
                            disabled={cid == null}
                            onChange={(e) =>
                              setDraftByContact((p) => ({
                                ...p,
                                [key]: { engrave: e.target.value, notes: p[key]?.notes ?? '' },
                              }))
                            }
                          />
                        )}
                        <TextField
                          size="small"
                          fullWidth
                          label="Notes"
                          placeholder="Notes"
                          value={draft.notes}
                          disabled={cid == null}
                          multiline
                          minRows={2}
                          onChange={(e) =>
                            setDraftByContact((p) => ({
                              ...p,
                              [key]: {
                                engrave: sponsorsMeta.show_engrave ? p[key]?.engrave ?? '' : '',
                                notes: e.target.value,
                              },
                            }))
                          }
                        />
                      </Box>
                      <Tooltip title="Save engrave & notes">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={cid == null || savingContactId === cid}
                            onClick={() => saveDetail(cid)}
                            aria-label="Save"
                          >
                            {savingContactId === cid ? <CircularProgress size={20} /> : <SaveIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={sponsorsMeta.total_pages}
                  page={sponsorPage}
                  onChange={(_, p) => setSponsorPage(p)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSponsors}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!ordersDialog}
        onClose={closeOrdersDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box>
            <Typography variant="h6">Orders (this product)</Typography>
            <Typography variant="body2" color="text.secondary">
              {ordersDialog?.sponsorName}
              {dialogRow ? ` · ${dialogRow.product_name}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Completed orders only (this product).
            </Typography>
          </Box>
          <IconButton onClick={closeOrdersDialog} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {ordersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : ordersRows.length === 0 ? (
            <Typography color="text.secondary">No orders found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Line total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordersRows.map((r) => (
                    <TableRow key={r.order_id} hover sx={{ cursor: 'pointer' }} onClick={() => openOrderRead(r.order_id)}>
                      <TableCell>
                        <Button
                          size="small"
                          variant="text"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openOrderRead(r.order_id);
                          }}
                          sx={{ textTransform: 'none', p: 0, minWidth: 0, fontWeight: 600 }}
                        >
                          #{r.order_id}
                        </Button>
                      </TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell>{fmtDate(r.date_created_gmt)}</TableCell>
                      <TableCell align="right">{money(r.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOrdersDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={orderReadId != null}
        onClose={closeOrderRead}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Typography variant="h6">Order #{orderReadId}</Typography>
          <IconButton onClick={closeOrderRead} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {orderReadLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {!orderReadLoading && orderReadErr && (
            <Alert severity="error" sx={{ mb: 1 }}>{orderReadErr}</Alert>
          )}
          {!orderReadLoading && !orderReadErr && orderReadPayload?.order && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Status &amp; dates
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {orderReadPayload.order.status || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong> {fmtDate(orderReadPayload.order.date_created_gmt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Paid:</strong> {fmtDate(orderReadPayload.order.date_paid_gmt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Completed:</strong> {fmtDate(orderReadPayload.order.date_completed_gmt)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  <strong>Order total:</strong> {money(orderReadPayload.order.total_num)}
                </Typography>
                <Typography variant="body2">
                  <strong>Payment:</strong> {orderReadPayload.order.payment_method_title || orderReadPayload.order.payment_method || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Billing
                </Typography>
                <Typography variant="body2">
                  {[orderReadPayload.order.billing_first_name, orderReadPayload.order.billing_last_name].filter(Boolean).join(' ') || '—'}
                </Typography>
                <Typography variant="body2">{orderReadPayload.order.billing_email || '—'}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {[
                    orderReadPayload.order.billing_address_1,
                    orderReadPayload.order.billing_address_2,
                    [orderReadPayload.order.billing_city, orderReadPayload.order.billing_state, orderReadPayload.order.billing_postcode]
                      .filter(Boolean)
                      .join(', '),
                    orderReadPayload.order.billing_country,
                  ]
                    .filter(Boolean)
                    .join('\n') || '—'}
                </Typography>
              </Box>
              {String(orderReadPayload.order.customer_note || '').trim() ? (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer note
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {orderReadPayload.order.customer_note}
                  </Typography>
                </Box>
              ) : null}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Line items (read-only)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orderReadPayload.items.map((it) => (
                        <TableRow key={it.id ?? `${it.order_id}-${it.item_id}`}>
                          <TableCell>{it.name || `Product #${it.product_id}`}</TableCell>
                          <TableCell align="right">{Number(it.quantity || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">{money(it.total_num)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOrderRead}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
